import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowUpRight, ArrowLeft, MapPin, CalendarClock, Check, FileDown, Share2, MessageCircle, Plus } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import VisitRequestModal from "@/components/VisitRequestModal";
import MediaLightbox from "@/components/MediaLightbox";
import useContentTranslation from "@/hooks/useContentTranslation";
import { buildMapEmbedUrl } from "@/lib/mapEmbed";
import { isVideoUrl } from "@/lib/media";
import { toast } from "sonner";
import { trackEvent } from "@/components/Analytics";
import { SkeletonProjectCard } from "@/components/Skeleton";
import useSEO from "@/hooks/useSEO";

const statusFilters = [
  { value: "all", label: "Tous" },
  { value: "realise", label: "Réalisés" },
  { value: "en_cours", label: "En cours" },
  { value: "futur", label: "À venir" },
];

function statusLabel(s) {
  return s === "realise" ? "Réalisé" : s === "en_cours" ? "En cours" : s === "futur" ? "À venir" : s;
}

export function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [search, setSearch] = useState("");
  useSEO({
    title: "Nos Projets Immobiliers",
    description: "Catalogue complet des projets AMAR SAHRAOUI : résidences réalisées, programmes en cours et projets futurs à Oran. Découvrez La Résidence ~THE GARDEN~ et nos autres réalisations.",
    path: "/projets",
  });

  useEffect(() => {
    api.get("/public/projects")
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => Array.from(new Set(projects.map((p) => p.category))).filter(Boolean), [projects]);
  const cities = useMemo(() => Array.from(new Set(projects.map((p) => p.city))).filter(Boolean), [projects]);
  const tl = useContentTranslation();

  const filtered = useMemo(
    () => projects.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
      if (cityFilter !== "all" && p.city !== cityFilter) return false;
      if (search && !`${p.name} ${p.city} ${p.program} ${p.short_description}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }),
    [projects, statusFilter, categoryFilter, cityFilter, search]
  );

  return (
    <div>
      {/* Hero */}
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16 border-b atelier-border-soft bg-atelier-deep relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" aria-hidden="true" />
        <div className="px-5 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="handwritten mb-3 animate-float">— INDEX DES RÉALISATIONS</div>
              <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-[1.02] text-atelier-text">
                Projets de <span className="italic text-atelier-ink">prestige.</span>
              </h1>
            </div>
            <div className="md:col-span-4 md:text-right">
              <span className="label-tech text-atelier-textDim">{filtered.length} projet(s) référencé(s)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section className="border-b atelier-border-soft bg-atelier-deep/80 backdrop-blur-md sticky top-16 z-30">
        <div className="px-5 lg:px-10 py-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un projet…"
            data-testid="projects-search"
            className="atelier-input flex-1 min-w-[200px] max-w-md"
          />
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="atelier-select" data-testid="projects-filter-category">
            <option value="all">Toutes catégories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="atelier-select" data-testid="projects-filter-city">
            <option value="all">Toutes villes</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex gap-1 flex-wrap">
            {statusFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                data-testid={`projects-filter-${f.value}`}
                className={`px-3 py-2 label-tech text-[0.65rem] border transition-all duration-300 ${
                  statusFilter === f.value ? "bg-atelier-ink text-atelier-deep border-atelier-ink" : "atelier-border-soft text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Folio grid */}
      <section className="py-12 lg:py-16 bg-atelier relative">
        <div className="px-5 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {loading && Array.from({ length: 4 }).map((_, i) => <SkeletonProjectCard key={i} />)}
          {!loading && filtered.map((p, idx) => (
            <Link
              key={p.id}
              to={`/projets/${p.slug}`}
              className="folio-reveal is-visible atelier-border p-6 flex flex-col gap-5 group hover:bg-atelier-surface/40 hover:border-atelier-ink/60 transition-all duration-500 relative scan-card"
              data-testid={`project-card-${p.slug}`}
            >
              <div className="flex justify-between items-start border-b atelier-border-soft pb-4">
                <div className="flex flex-col">
                  <span className="label-tech text-atelier-ink animate-pulse-soft">PROJET N° {String(idx + 1).padStart(3, "0")}</span>
                  <h3 className="font-serif text-2xl lg:text-3xl text-atelier-text mt-1 group-hover:text-atelier-ink transition-colors">{tl(p, "name")}</h3>
                </div>
                <div className="handwritten text-right">
                  {statusLabel(p.status).toUpperCase()}<br />
                  {p.launch_date ? `LANCÉ ${p.launch_date}` : ""}
                </div>
              </div>

              <div className="relative overflow-hidden aspect-video bg-atelier-surface proj-img-shell tech-corners">
                <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 atelier-border bg-atelier-deep/80 backdrop-blur-sm px-2.5 py-1">
                  <span className="label-tech text-[0.55rem] text-atelier-ink flex items-center gap-1.5">
                    <MapPin className="w-2.5 h-2.5" /> {p.city}, {p.wilaya}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 border-b atelier-border-soft pb-5">
                <div>
                  <p className="label-tech text-[0.55rem] text-atelier-textFaint mb-1">Catégorie</p>
                  <p className="text-sm text-atelier-text font-serif capitalize">{p.category || "—"}</p>
                </div>
                <div>
                  <p className="label-tech text-[0.55rem] text-atelier-textFaint mb-1">Unités</p>
                  <p className="text-sm text-atelier-text font-serif">{p.units_count || "—"}</p>
                </div>
                <div>
                  <p className="label-tech text-[0.55rem] text-atelier-textFaint mb-1">Avancement</p>
                  <p className="text-sm text-atelier-text font-serif">{p.progress_percent ? `${p.progress_percent}%` : "—"}</p>
                </div>
              </div>

              <p className="text-sm text-atelier-textDim line-clamp-2 font-serif">{tl(p, "short_description")}</p>

              <div className="flex items-center justify-between mt-auto">
                <span className="label-tech text-atelier-ink flex items-center gap-2 group-hover:gap-3 transition-all">
                  <Plus className="w-3 h-3" /> Voir le projet
                </span>
                <ArrowUpRight className="w-4 h-4 text-atelier-textDim group-hover:text-atelier-ink transition-colors" />
              </div>
            </Link>
          ))}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full atelier-border border-dashed p-16 text-center text-atelier-textFaint">
              <span className="label-tech">Aucun projet ne correspond à vos critères.</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [visitOpen, setVisitOpen] = useState(false);
  const { contact } = useSite();
  const tl = useContentTranslation();
  useSEO({
    title: project ? tl(project, "name") : "Projet immobilier",
    description: project ? tl(project, "short_description") : undefined,
  });

  const mediaItems = useMemo(() => {
    if (!project) return [];
    const seen = new Set();
    const pushUnique = (url) => {
      if (!url || seen.has(url)) return null;
      seen.add(url);
      return { url };
    };
    const photos = (project.gallery || []).map(pushUnique).filter(Boolean);
    const videos = (project.videos || []).map(pushUnique).filter(Boolean);
    return [...photos, ...videos];
  }, [project]);

  const copyWhatsAppLink = () => {
    if (!project) return;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const text = `🏢 ${tl(project, "name")} — ${tl(project, "short_description")}\n\n${url}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => {
          toast.success("Lien WhatsApp copié — collez-le dans votre conversation");
          trackEvent("project_share", { platform: "whatsapp_copy", project_name: project.name });
        },
        () => toast.error("Impossible de copier")
      );
    }
  };

  useEffect(() => {
    api.get(`/public/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="py-32 text-center">
      <h2 className="font-serif text-4xl mb-4 text-atelier-text">Projet introuvable</h2>
      <Link to="/projets" className="btn-primary inline-flex">Voir nos projets</Link>
    </div>
  );
  if (!project) return <div className="py-32 text-center text-atelier-textDim">Chargement…</div>;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[65vh] min-h-[480px] flex items-end overflow-hidden bg-atelier-deep">
        <img src={project.cover_image} alt={project.name} className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-atelier-deep via-atelier-deep/50 to-transparent" />
        <div className="absolute inset-0 blueprint-grid opacity-15" aria-hidden="true" />
        <div className="relative px-5 lg:px-10 pb-14 w-full text-atelier-text">
          <Link to="/projets" className="inline-flex items-center gap-2 label-tech text-atelier-textDim hover:text-atelier-ink transition-colors mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Tous les projets
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="pill border-atelier-ink/50 text-atelier-ink bg-atelier-deep/60">{statusLabel(project.status)}</span>
            <span className="label-tech text-atelier-ink flex items-center gap-2"><MapPin className="w-3 h-3" /> {project.city}, {project.wilaya}</span>
          </div>
          <h1 className="font-serif text-4xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.02] max-w-5xl">{tl(project, "name")}</h1>
          <p className="mt-6 text-atelier-textDim text-lg max-w-2xl font-serif">{tl(project, "short_description")}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20 bg-atelier">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            <div className="label-tech text-atelier-ink mb-3">— Le programme</div>
            <h2 className="font-serif text-3xl lg:text-4xl mb-6 text-atelier-text">{project.program}</h2>
            <p className="text-base leading-relaxed text-atelier-textDim whitespace-pre-line font-serif">{tl(project, "long_description")}</p>

            {project.highlights?.length > 0 && (
              <div className="mt-12">
                <div className="label-tech text-atelier-ink mb-4">— Points forts</div>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {project.highlights.map((h) => (
                    <li key={h} className="flex gap-3 text-sm border-l-2 border-atelier-ink pl-4 py-1 text-atelier-text font-serif">
                      <Check className="w-4 h-4 text-atelier-ink flex-shrink-0 mt-0.5" strokeWidth={1.4} /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {project.amenities?.length > 0 && (
              <div className="mt-12">
                <div className="label-tech text-atelier-ink mb-4">— Équipements & commodités</div>
                <div className="flex flex-wrap gap-2">
                  {project.amenities.map((a) => (
                    <span key={a} className="px-4 py-2 atelier-border text-sm text-atelier-text">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="md:col-span-4">
            <div className="atelier-border bg-atelier-surface/40 p-6 lg:p-7 space-y-6">
              <div>
                <div className="label-tech text-atelier-textFaint mb-2">État d'avancement</div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-serif text-3xl text-atelier-ink">{project.progress_percent}%</span>
                  <span className="text-sm text-atelier-textDim">complété</span>
                </div>
                <div className="h-px bg-atelier-line">
                  <div className="h-full bg-atelier-ink transition-all duration-700" style={{ width: `${project.progress_percent}%` }} />
                </div>
              </div>
              {project.launch_date && <div><div className="label-tech text-atelier-textFaint mb-1">Lancement</div><div className="font-serif text-lg text-atelier-text">{project.launch_date}</div></div>}
              {project.delivery_date && <div><div className="label-tech text-atelier-textFaint mb-1">Livraison prévue</div><div className="font-serif text-lg text-atelier-text">{project.delivery_date}</div></div>}
              {project.units_count && <div><div className="label-tech text-atelier-textFaint mb-1">Logements</div><div className="font-serif text-lg text-atelier-text">{project.units_count}</div></div>}
              {project.typologies?.length > 0 && (
                <div>
                  <div className="label-tech text-atelier-textFaint mb-2">Typologies</div>
                  <div className="flex flex-wrap gap-1.5">
                    {project.typologies.map((t) => <span key={t} className="px-2.5 py-1 atelier-border text-xs text-atelier-text">{t}</span>)}
                  </div>
                </div>
              )}
              {project.surfaces?.length > 0 && (
                <div>
                  <div className="label-tech text-atelier-textFaint mb-2">Surfaces</div>
                  <div className="flex flex-wrap gap-1.5">
                    {project.surfaces.map((s) => <span key={s} className="px-2.5 py-1 atelier-border text-xs text-atelier-text">{s}</span>)}
                  </div>
                </div>
              )}
              <Link to={`/contact?projet=${encodeURIComponent(project.name)}`} className="btn-primary w-full justify-center" data-testid="project-cta-contact">
                Demander des informations
              </Link>
              <button onClick={() => setVisitOpen(true)} className="btn-outline w-full justify-center" data-testid="project-cta-visit">
                <CalendarClock className="w-4 h-4" /> Demander une visite
              </button>
              {project.brochure_url && (
                <a href={project.brochure_url} onClick={() => trackEvent("brochure_download", { project_name: project.name })} target="_blank" rel="noreferrer" className="btn-outline w-full justify-center">
                  <FileDown className="w-4 h-4" /> Télécharger la brochure
                </a>
              )}
              <div className="pt-4 border-t atelier-border-soft">
                <div className="label-tech text-atelier-textFaint mb-3 flex items-center gap-2"><Share2 className="w-3 h-3" /> Partager</div>
                <button
                  type="button"
                  onClick={copyWhatsAppLink}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3 label-tech hover:bg-atelier-ink hover:text-atelier-deep transition-colors mb-2"
                  data-testid="project-share-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> Copier le lien WhatsApp
                </button>
                <div className="flex gap-2">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} onClick={() => trackEvent("project_share", { platform: "facebook", project_name: project.name })} target="_blank" rel="noreferrer" className="flex-1 text-center px-3 py-1.5 atelier-border-soft text-xs text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink transition-all">Facebook</a>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`${project.name} — ${shareUrl}`)}`} onClick={() => trackEvent("project_share", { platform: "whatsapp", project_name: project.name })} target="_blank" rel="noreferrer" className="flex-1 text-center px-3 py-1.5 atelier-border-soft text-xs text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink transition-all">WhatsApp</a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} onClick={() => trackEvent("project_share", { platform: "linkedin", project_name: project.name })} target="_blank" rel="noreferrer" className="flex-1 text-center px-3 py-1.5 atelier-border-soft text-xs text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink transition-all">LinkedIn</a>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Gallery */}
      {mediaItems.length > 0 && (
        <section className="py-16 lg:py-20 border-t atelier-border-soft bg-atelier-deep">
          <div className="px-5 lg:px-10">
            <div className="label-tech text-atelier-ink mb-3">— Galerie</div>
            <h2 className="font-serif text-3xl lg:text-5xl mb-10 text-atelier-text">Plongez dans le projet.</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {mediaItems.map((item, i) => {
                const isVid = isVideoUrl(item.url);
                return (
                  <button key={i} onClick={() => setLightboxIndex(i)} className="aspect-square overflow-hidden bg-atelier-surface proj-img-shell relative group" data-testid={`project-gallery-${i}`}>
                    {isVid ? (
                      <>
                        <video src={item.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-atelier-deep/40 group-hover:bg-atelier-deep/20 transition">
                          <div className="w-14 h-14 bg-atelier-ink/90 flex items-center justify-center">
                            <svg viewBox="0 0 24 24" className="w-6 h-6 ml-1 text-atelier-deep" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <img src={item.url} alt={`${project.name} ${i + 1}`} className="w-full h-full object-cover" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Map */}
      {(() => {
        const mapUrl = buildMapEmbedUrl(project);
        if (!mapUrl) return null;
        return (
          <section className="py-16 lg:py-20 bg-atelier">
            <div className="px-5 lg:px-10">
              <div className="label-tech text-atelier-ink mb-3">— Localisation</div>
              <h2 className="font-serif text-3xl lg:text-5xl mb-8 text-atelier-text">{project.address}</h2>
              <div className="aspect-[16/9] atelier-border">
                <iframe src={mapUrl} title="Carte" className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </div>
          </section>
        );
      })()}

      {/* Linked availabilities */}
      {project.availabilities?.length > 0 && (
        <section className="py-16 lg:py-20 bg-atelier-deep border-t atelier-border-soft">
          <div className="px-5 lg:px-10">
            <div className="label-tech text-atelier-ink mb-3">— Disponibilités liées</div>
            <h2 className="font-serif text-3xl lg:text-5xl mb-10 text-atelier-text">Logements à <span className="italic text-atelier-ink">réserver.</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 atelier-border">
              {project.availabilities.map((a) => (
                <div key={a.id} className="bg-atelier-surface/30 p-6 border-r border-b atelier-border-soft">
                  <div className="label-tech text-atelier-ink mb-2">{a.reference}</div>
                  <div className="font-serif text-xl mb-2 text-atelier-text">{a.surface}</div>
                  <div className="text-sm text-atelier-textDim mb-3 font-serif">{a.type} {a.rooms ? `· ${a.rooms} pièces` : ""}</div>
                  <span className={`pill ${a.status === "disponible" ? "text-atelier-ink" : a.status === "reserve" ? "text-amber-400" : "text-atelier-textFaint"}`}>{a.status === "disponible" ? "Disponible" : a.status === "reserve" ? "Réservé" : "Vendu"}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Similar */}
      {project.similar?.length > 0 && (
        <section className="py-16 lg:py-20 bg-atelier">
          <div className="px-5 lg:px-10">
            <div className="label-tech text-atelier-ink mb-3">— À découvrir aussi</div>
            <h2 className="font-serif text-3xl lg:text-5xl mb-10 text-atelier-text">Projets similaires.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {project.similar.map((s) => (
                <Link key={s.id} to={`/projets/${s.slug}`} className="block proj-img-shell atelier-border hover:border-atelier-ink/60 transition-all duration-500 scan-card bg-atelier-surface/30">
                  <div className="aspect-[4/3] overflow-hidden"><img src={s.cover_image} alt={s.name} className="w-full h-full object-cover" /></div>
                  <div className="p-6">
                    <div className="label-tech text-atelier-ink mb-2">{statusLabel(s.status)}</div>
                    <div className="font-serif text-xl text-atelier-text">{s.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightboxIndex !== null && (
        <MediaLightbox
          items={mediaItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}

      <VisitRequestModal open={visitOpen} onClose={() => setVisitOpen(false)} project={project} />
    </div>
  );
}
